import React, { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useMenuStore } from '../zustand/store/useMenuStore';
import useConfirm from '../hooks/useConfirm';
import useQueryLinkNavigate from '../hooks/useQueryLinkNavigate';
import { useModalOpen } from '../hooks/useToastModalOpen';
import { useShallow } from 'zustand/shallow';

import { useDynamicStyle } from '../hooks/useDynamicStyle';
import { useSubIcon } from '../hooks/useSubIcon';
import * as Icons from '../images/icons/icons_component';
import * as Theme from '../images/icons/icons_theme';

const Favorite = ({isSidebarToggle, favMenuOpen, setFavMenuOpen, homeRef, favMenuRef, favBtnRef, nextRef, handleSmallMoniterMenuClick}) => {

    const { isHome } = useQueryLinkNavigate();
    const navigate = useNavigate();
    
    // Dynamic Style Hook
    const { fontColor, hoverFontColor, favBorderColor, lightBackColor } = useDynamicStyle();

    // get favorite menu function
    const favMenuData = useMenuStore(state => state.favMenuData);
    
    // get toast modal open function
    const { toastModalOpen } = useModalOpen();

    const [ categoryCodes, favoriteMenuData ] = useMenuStore(useShallow((state) => [state.categoryCodes, state.favoriteMenuData]));
    
    // confirm hook
    const confirm = useConfirm();
    
    const favoriteMenuList = Object.values(favoriteMenuData || {});
    const favoritesGroups = categoryCodes.map(code =>
        favoriteMenuList?.filter(menu => menu.SYTMIDNO === code)
    );

    const favMenuDelete = async (title, menuflag, symnidno) => {
        try {
            const ok = await confirm("즐겨찾기에서 '" + title + "' 메뉴를 삭제하시겠습니까?");
            if(!ok) return false;

            await axios.delete('/ptmiapi/deleteFavoriteMenu.do', {
                data: { menuflag : String(menuflag), symnidno : symnidno }
            });
            toastModalOpen('즐겨찾기에서 메뉴가 삭제되었습니다.');
            favMenuData();
        } catch (error) {
            console.error('즐겨찾기 메뉴 삭제 중 오류 발생:', error);
        }
    };
    
    // icon list
    const { favHereIcon } = useSubIcon();

    // 즐겨찾기 목록의 각 행 요소들을 참조하기 위한 배열 ref
    const itemRefs = useRef([]);
    itemRefs.current = [];
    // 삭제버튼, 메뉴 리스트를 ref에 차례대로 담기(삭제버튼: 짝수index, list: 홀수index)
    const addToRefs = (el) => {
        if(el && !itemRefs.current.includes(el)) {
            itemRefs.current.push(el);
        }
    };
    
    // 화살표 키 조작 핸들러
    const handleKeyDown = (e, menu, type) => {
        const totalRefs = itemRefs.current.length;
        if (totalRefs === 0) return;
        const currentIndex = itemRefs.current.indexOf(e.currentTarget);
        if (currentIndex === -1) return;
        
        const deleteBtn = type === 'delete';

        switch (e.key) {
            case 'ArrowDown': // 아래 화살표: 다음 줄로 이동(마지막이면 처음으로 루프)
                e.preventDefault();
                const nextBtn = itemRefs.current[currentIndex + 2] || itemRefs.current[(currentIndex % 2 === 0 ? 0 : 1)];
                nextBtn.focus();
                break;

            case 'ArrowUp': // 위 화살표: 이전 줄로 이동(처음이면 마지막으로 루프)
                e.preventDefault();
                const prevBtn = itemRefs.current[currentIndex - 2] || itemRefs.current[(currentIndex % 2 === 0 ? totalRefs - 2 : totalRefs - 1)];
                prevBtn.focus();
                break;

            case 'ArrowRight': // 오른쪽 화살표: 삭제 버튼에서 링크로 이동
                if (e.currentTarget.classList.contains('fav_delete_btn')) {
                    e.preventDefault();
                    if (currentIndex + 1 < totalRefs) {
                        itemRefs.current[currentIndex + 1].focus();
                    }
                }
                break;

            case 'ArrowLeft': // 왼쪽 화살표: 링크에서 삭제 버튼으로 이동
                if (e.currentTarget.classList.contains('fav_menu_list')) {
                    e.preventDefault();
                    if (currentIndex - 1 >= 0) {
                        itemRefs.current[currentIndex - 1].focus();
                    }
                }
                break;

            default:
                break;
            }

        if (e.key === 'Enter' || e.key === ' ') {
            if(deleteBtn) {
                favMenuDelete(menu.MENUNAME, menu.MENUFLAG, menu.SYTMIDNO);
            } else {
                navigate(menu.MENUSORC);
                handleSmallMoniterMenuClick();
            }
            setFavMenuOpen(false);
        }
        if(!isHome && e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();
            if (homeRef && homeRef.current) {
                homeRef.current.focus();
                setFavMenuOpen(false);
            }
        }
        if(e.key === 'Escape') {
            if (favBtnRef && favBtnRef.current) {
                favBtnRef.current.focus();
                setFavMenuOpen(false);
            }
        }
    };
    
    // 메뉴 어디에 있든 tab을 누르면 다음 버튼으로 나갈 수 있도록 지정
    const handleTabExit = (e) => {
        if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();
            if (nextRef && nextRef.current) {
                nextRef.current.focus();
            }
        }
    };
    
    const firstItemId = favoritesGroups?.find(item => item && item.length > 0)?.[0]?.MENUFLAG;
    
    const renderFavoriteMenus = (filteredData) => {
        return filteredData.map((menu) => {
            const firstItem = firstItemId && firstItemId === menu.MENUFLAG
            return (
                <div key={menu.MENUFLAG} role='listitem' className='fav_wrapper' data-id={menu.MENUFLAG} aria-label='즐겨찾기 목록'>
                    <button ref={addToRefs} tabIndex={-1}
                            className='fav_delete_btn h_icon_box'
                            aria-label={`${menu.MENUNAME} 메뉴 즐겨찾기 삭제`}
                            onClick={() => favMenuDelete(menu.MENUNAME, menu.MENUFLAG, menu.SYTMIDNO)}
                            onKeyDown={(e) => {handleKeyDown(e, menu, 'delete'); handleTabExit(e);}}>
                        <div className='icon'><Theme.FavDelete16 className='co_gray' /></div>
                        <div className='hover_icon'><Theme.FavDelete16 className='co_dark' /></div>
                    </button>
                    <li ref={addToRefs} tabIndex={firstItem ? 0 : -1}
                        className={`fav_menu_list ${hoverFontColor}`}
                        onClick={() => {navigate(menu.MENUSORC); handleSmallMoniterMenuClick();}}
                        onKeyDown={(e) => {handleKeyDown(e, menu, 'link'); handleTabExit(e);}}
                        >
                        {menu.MENUNAME}
                        <div className='fav_here_icon'>{favHereIcon}</div>
                    </li>
                </div>
        )});
    };
    
    const renderedFavorites = favoritesGroups.reduce((acc, group, idx) => {
        if (group.length > 0) {
            if(group[0].SYTMIDNO === 3) {
                acc.push(<div key='3' className={`fav_top_title ${fontColor} ${lightBackColor}`}>표준등록체계</div>);
            } else if(group[0].SYTMIDNO === 21) {
                acc.push(<div key='21' className={`fav_top_title ${fontColor} ${lightBackColor}`}>외상등록체계</div>);
            } else {
                acc.push(<div key='9' className={`fav_top_title ${fontColor} ${lightBackColor}`}>게시판 및 관리</div>);
            }
            acc.push(<React.Fragment key={`group-${idx}`}>{renderFavoriteMenus(group)}</React.Fragment>);
        }
        return acc;
    }, []);

    // useRef current에 담긴 엘리먼트 바깥(favorite menu 선택 외 영역)을 클릭 시 메뉴 닫힘
    useEffect(() => {
        const handleOutsideClose = (e) => {
            if(!favMenuRef.current?.contains(e.target) && !favBtnRef.current?.contains(e.target)) {
                setFavMenuOpen(false);
            };
            if(favMenuRef.current?.contains(e.target)) {
                setFavMenuOpen(false);
            };
        };
        document.addEventListener('click', handleOutsideClose);
        
        return () => document.removeEventListener('click', handleOutsideClose);
    }, [favMenuOpen]);

    return (
        <>
            <div ref={favMenuRef} tabIndex={-1}
                onTransitionEnd={() => {
                    if(!isHome && favMenuOpen && favMenuRef.current) {
                        console.log('onTransitionEnd')
                        favMenuRef.current.focus();
                    }
                }}
                className={isHome ? `home_fav_container` 
                        : `fav_container ${favBorderColor} ${(renderedFavorites.length < 1) ? 'overflow-x-visible' : ''} 
                            ${isSidebarToggle ? 'fav_side_open' : 'fav_side_close fav_menu_position'}`}>
                {!isHome ? '' :
                    <div className='fav_title_wrapper mb-3'>
                        <Icons.HomeFavStar /><span className='fav_title'>즐겨찾기</span>
                    </div>
                }
                <div className='overflow-x-auto hp-90' role='list' aria-label='즐겨찾기 목록'>
                    {(renderedFavorites.length < 1) && (
                        <div className='fav_non'>
                            <Icons.BookmarkDalgu className='mx-auto' />
                            <p className='bold co_gray'>자주 찾는 메뉴를 즐겨찾기로 지정할 수 있습니다.</p>
                        </div>
                    )}
                    {renderedFavorites}
                </div>
            </div>
        </>
    )
}

export default Favorite